/**
 * Subscription Intent Management
 * 
 * Stores subscription intent in sessionStorage so that after signup/login,
 * the user is redirected to Stripe checkout instead of the account page.
 */

const SUBSCRIPTION_INTENT_KEY = 'subscription_intent';

export interface SubscriptionIntent {
  product: 'industry_insights';
  priceId: string;
  timestamp: number;
  returnTo?: string;
}

// Intent expires after 30 minutes
const INTENT_TTL_MS = 30 * 60 * 1000;

/**
 * Set subscription intent before redirecting to auth
 */
export function setSubscriptionIntent(intent: Omit<SubscriptionIntent, 'timestamp'>): void {
  const fullIntent: SubscriptionIntent = {
    ...intent,
    timestamp: Date.now(),
  };
  sessionStorage.setItem(SUBSCRIPTION_INTENT_KEY, JSON.stringify(fullIntent));
}

/**
 * Get and validate subscription intent
 * Returns null if no intent exists or if it has expired
 */
export function getSubscriptionIntent(): SubscriptionIntent | null {
  try {
    const stored = sessionStorage.getItem(SUBSCRIPTION_INTENT_KEY);
    if (!stored) return null;

    const intent: SubscriptionIntent = JSON.parse(stored);
    
    // Check if intent has expired
    if (Date.now() - intent.timestamp > INTENT_TTL_MS) {
      clearSubscriptionIntent();
      return null;
    }

    return intent;
  } catch {
    return null;
  }
}

/**
 * Clear subscription intent after it has been processed
 */
export function clearSubscriptionIntent(): void {
  sessionStorage.removeItem(SUBSCRIPTION_INTENT_KEY);
}

/**
 * Check if there's a valid subscription intent
 */
export function hasSubscriptionIntent(): boolean {
  return getSubscriptionIntent() !== null;
}
